import * as React from "react";

import Image, { type StaticImageData } from "next/image";

import type { Control, FieldValues, Path } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@repo/ui/components/ui/radio-group2";
import { cn } from "@repo/ui/lib/utils";

export interface RadioOption {
  label: string | React.ReactNode;
  value: string;
  disabled?: boolean;
  image?: StaticImageData | string | undefined;
  width?: number;
  height?: number;
  testId?: string;
}

export type RadioVariant = "blue" | "white" | "orange" | "cyan";

export interface RadioGroupFieldProps<T extends FieldValues = FieldValues> extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "type"
> {
  label?: string;
  description?: string;
  error?: string;
  success?: string;
  options: RadioOption[];
  orientation?: "vertical" | "horizontal";
  variant?: RadioVariant;
  control?: Control<T>;
  name?: Path<T>;
  size?: "2" | "3";
  defaultValue?: string;
  onValueChange?: (val: string) => void;
  radioGroupClass?: string;
  radioOptionClass?: string;
  testId?: string;
}

const variantStyles: Record<RadioVariant, string> = {
  blue: "border-brand-blue data-[state=checked]:border-brand-blue [&_svg]:fill-brand-blue [&_svg]:stroke-brand-blue",
  white: "data-[state=checked]:border-white [&_svg]:fill-white [&_svg]:stroke-white",
  orange:
    "data-[state=checked]:border-brand-orange [&_svg]:fill-brand-orange [&_svg]:stroke-brand-orange",
  cyan: "data-[state=checked]:border-brand-cyan [&_svg]:fill-brand-cyan [&_svg]:stroke-brand-cyan",
};

const variantSize = {
  2: "size-4 [&_svg]:size-2",
  3: "size-6 border-2 [&_svg]:size-3",
};

function RadioGroupFieldInner<T extends FieldValues = FieldValues>(
  {
    className,
    label,
    description,
    options,
    orientation = "vertical",
    control,
    name,
    error,
    success,
    variant = "blue",
    size = "2",
    defaultValue,
    onValueChange,
    radioGroupClass,
    radioOptionClass,
    testId,
  }: RadioGroupFieldProps<T>,
  ref: React.Ref<HTMLDivElement>,
) {
  const layout = orientation === "horizontal" ? "flex flex-row gap-4" : "flex flex-col gap-2";

  const radioItemClass = cn(
    "focus-visible:ring-2 focus-visible:ring-offset-2",
    variantSize[size],
    variantStyles[variant],
  );

  // RHF mode
  if (control && name) {
    return (
      <FormField<T>
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <FormItem className={cn("space-y-2", className)} data-testid={testId}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <RadioGroup
                ref={ref}
                value={field.value}
                onValueChange={field.onChange}
                className={layout + ` ${radioGroupClass}`}
                aria-invalid={!!fieldState.error}
              >
                {options.map((opt) => (
                  <div key={opt.value} className={`flex items-center gap-2 ${radioOptionClass}`}>
                    <RadioGroupItem
                      id={`${String(name)}-${opt.value}`}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={radioItemClass}
                      data-testid={opt.testId ?? `${opt.value}-radio`}
                    />
                    <label
                      htmlFor={`${String(name)}-${opt.value}`}
                      className="text-sm flex"
                      data-testid={`${opt.testId || `${opt.value}-radio`}-label`}
                    >
                      {opt.label}
                      {opt?.image && (
                        <Image
                          className="ml-1"
                          src={opt.image}
                          alt="client"
                          width={opt.width}
                          height={opt.height}
                        />
                      )}
                    </label>
                  </div>
                ))}
              </RadioGroup>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // Standalone mode
  return (
    <div ref={ref} className={cn("space-y-2", className)} data-testid={testId}>
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      <RadioGroup
        className={layout}
        aria-invalid={!!error}
        onValueChange={onValueChange}
        defaultValue={defaultValue}
      >
        {options.map((opt) => (
          <div key={opt.value} className="flex items-center gap-2">
            <RadioGroupItem
              id={`radio-${opt.value}`}
              value={opt.value}
              disabled={opt.disabled}
              className={radioItemClass}
              data-testid={opt.testId || `${opt.value}-radio`}
            />
            <label
              htmlFor={`radio-${opt.value}`}
              className="text-sm"
              data-testid={`${opt.testId || `${opt.value}-radio`}-label`}
            >
              {opt.label}
            </label>
          </div>
        ))}
      </RadioGroup>
      {description && <p className="text-xs text-gray-500">{description}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
      {success && <p className="text-xs text-green-500">{success}</p>}
    </div>
  );
}

export const RadioGroupField = React.forwardRef(RadioGroupFieldInner) as <
  T extends FieldValues = FieldValues,
>(
  props: RadioGroupFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement;

(RadioGroupField as unknown as { displayName?: string }).displayName = "RadioGroupField";
